import { api, API_BASE, ApiError } from './client'

export interface ReportChart {
  id: string
  title: string
  unit: string
  labels: string[]
  series: { label: string; values: (number | null)[] }[]
  note: string
  kind: 'line' | 'bar'
  minimum?: number
  maximum: number | null
}
export interface ReportTrial {
  id: string
  session_id: string
  number: number
  label: string
  scenario: string
  scenario_label: string
  context_label: string | null
  phase: string | null
  outcome: string
  outcome_label: string
  score: number | null
  started_at: string | null
  deadline_s: number | null
  response_s: number | null
  expected_action?: 'hold' | 'pass' | null
  decision_correct?: boolean | null
  execution_correct?: boolean | null
  hr_peak: number | null
  hr_recovery: number | null
  hr_drop: number | null
  rmssd: number | null
  reference_rmssd: number | null
  signal_note: string
  formula_version: string | null
  eligible: boolean
}
export interface ReportPerformance {
  success_rate: number | null
  successes: number
  resolved: number
  scenario: string
  label: string
  trials: number
  attempts: number
  goals: number
  goal_rate: number | null
  outcomes: { label: string; count: number }[]
}
export interface ReportSession {
  id: string
  date: string
  status: string
  duration_min: number | null
  protocol: string | null
  condition: string | null
  trials: ReportTrial[]
  performance: ReportPerformance[]
  warning: string | null
  sleep_hours: number | null
  caffeine: boolean | null
  hours_since_training: number | null
  reflection?: {
    arousal: number | null
    control: number | null
    notes: string | null
    date: string
  } | null
}
export type SignalTone = 'green' | 'amber' | 'red' | 'gray'
export interface ReportSignal {
  title: string
  tone: SignalTone
  status: string
  date: string | null
  source: string
  summary: string
  action: string
}
export interface QuestionnaireReading {
  version: string
  note: string
  total: { label: string; raw: number; min: number; max: number } | null
  dimensions: {
    key: string
    label: string
    raw: number
    min: number
    max: number
    mean: number
    tone: SignalTone
    status: string
    action: string
  }[]
}
export interface EvidenceRef {
  session_id: string
  trial_id: string
  number: number
}
export interface WorkFocus {
  title: string
  tone: SignalTone
  status: string
  summary: string
  basis: string
  action: string
  check: string
  evidence: EvidenceRef[]
  comparison_evidence: EvidenceRef[]
  count: number
}
export interface ReportEvidence {
  profile_note: string
  method: string
  profile: {
    code: string
    title: string
    date: string | null
    timepoint: string | null
    total: QuestionnaireReading['total']
    note: string
    availability: string
    dimensions: {
      key: string
      label: string
      meaning: string
      score: QuestionnaireReading['dimensions'][number] | null
      previous_mean: number | null
      previous_date: string | null
      change: number | null
    }[]
  }[]
  practice: {
    skills: {
      key: string
      title: string
      successes: number
      total: number
      note: string
      evidence: EvidenceRef[]
    }[]
    timing: {
      exercise: string
      deadline_s: number
      count: number
      median_response_s: number
      successes: number
      evidence: EvidenceRef[]
    }[]
    after_error: {
      successes: number
      total: number
      note: string
      evidence: EvidenceRef[]
    }
  }
  plan: WorkFocus[]
}
export interface PlayerReport {
  version: string
  generated_at: string
  scope: 'global' | 'session' | 'trial'
  title: string
  player: string
  session_id: string | null
  trial_id: string | null
  date_from: string | null
  date_to: string | null
  total_sessions: number
  truncated: boolean
  sessions: ReportSession[]
  trials: ReportTrial[]
  performance: ReportPerformance[]
  trial_count: number
  signal_count: number
  charts: ReportChart[]
  coaching: {
    headline: string
    summary: string
    focus: {
      title: string
      observation: string
      evidence: string
      action: string
      session_id: string | null
      trial_id: string | null
    }
    resolved: number
    successes: number
    unresolved: number
    measured: number
    held: number
    sample_note: string
    physiology_note: string
  }
  evidence: ReportEvidence
  reading: {
    signals: ReportSignal[]
    method: string
    progress: ReportSignal & {
      session_id: string | null
      note: string
      comparison: {
        first: number
        last: number
        n: number
        delta_pp: number
        included: number
        total: number
        first_trials: number[]
        last_trials: number[]
      } | null
    }
  }
  insights: string[]
  questionnaires: {
    code: string
    name: string
    timepoint: string
    date: string | null
    status: string
    reading?: QuestionnaireReading | null
    scores: { label: string; raw: number; min: number; max: number }[] | null
  }[]
  scores_visible: boolean
  questionnaire_note: string
  protocol: { active: boolean; ended_at: string | null; note: string }
  notes: string[]
}
export function reportQuery(
  sessionId?: string,
  trialId?: string,
  from?: string,
  to?: string,
) {
  const params = new URLSearchParams()
  if (sessionId) params.set('session_id', sessionId)
  if (trialId) params.set('trial_id', trialId)
  if (!sessionId && from) params.set('date_from', from)
  if (!sessionId && to) params.set('date_to', to)
  return params.toString()
}
export const getPlayerReport = (query: string) =>
  api.get<PlayerReport>(`/know/me/reports?${query}`)

export async function downloadPlayerReport(query: string, scope: string) {
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), 60_000)
  try {
    const response = await fetch(
      `${API_BASE}/know/me/reports/report.pdf?${query}`,
      { credentials: 'include', signal: controller.signal },
    )
    if (!response.ok)
      throw new ApiError(
        response.status,
        null,
        'No pudimos generar el PDF. Volvé a intentar.',
      )
    if (!response.headers.get('content-type')?.includes('application/pdf'))
      throw new Error('La descarga no devolvió un PDF válido.')
    const url = URL.createObjectURL(await response.blob())
    const link = document.createElement('a')
    link.href = url
    link.download = `versports-${scope}.pdf`
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.setTimeout(() => URL.revokeObjectURL(url), 30_000)
  } catch (error) {
    if (controller.signal.aborted)
      throw new Error(
        'La generación tardó demasiado. Acotá las fechas y volvé a intentar.',
      )
    throw error
  } finally {
    window.clearTimeout(timer)
  }
}

export const saveSessionReflection = (
  sessionId: string,
  values: { arousal: number; control: number; notes: string },
) =>
  api.post<{ saved: boolean }>(
    `/know/me/reports/sessions/${encodeURIComponent(sessionId)}/reflection`,
    values,
  )
